'use client'

import { PublicationDraft } from '@/lib/publications/types'

import TemplateSingleHero from './templates/TemplateSingleHero'
import TemplateCarouselPremium from './templates/TemplateCarouselPremium'

import CreatorAnnouncementPack from './packs/creator/CreatorAnnouncementPack'
import CreatorAuthorityPack from './packs/creator/CreatorAuthorityPack'
import CreatorCarouselPack from './packs/creator/CreatorCarouselPack'
import CreatorCommunityPack from './packs/creator/CreatorCommunityPack'
import CreatorEducationalPack from './packs/creator/CreatorEducationalPack'
import CreatorOfferConversionPack from './packs/creator/CreatorOfferConversionPack'
import CreatorStoryPack from './packs/creator/CreatorStoryPack'
import CreatorTestimonialPack from './packs/creator/CreatorTestimonialPack'

import BeautyAnnouncementPack from './packs/beauty/BeautyAnnouncementPack'
import BeautyAuthorityPack from './packs/beauty/BeautyAuthorityPack'
import BeautyCommunityPack from './packs/beauty/BeautyCommunityPack'
import BeautyEducationalRoutinePack from './packs/beauty/BeautyEducationalRoutinePack'
import BeautyLuxuryOfferPack from './packs/beauty/BeautyLuxuryOfferPack'
import BeautyStoryPack from './packs/beauty/BeautyStoryPack'
import BeautyTestimonialPack from './packs/beauty/BeautyTestimonialPack'

import CorporateAuthorityPack from './packs/corporate/CorporateAuthorityPack'
import CorporateCarouselPack from './packs/corporate/CorporateCarouselPack'
import CorporateCaseStudyTestimonialPack from './packs/corporate/CorporateCaseStudyTestimonialPack'
import CorporateCommunityPack from './packs/corporate/CorporateCommunityPack'
import CorporateEducationalPack from './packs/corporate/CorporateEducationalPack'
import CorporateExecutiveAnnouncementPack from './packs/corporate/CorporateExecutiveAnnouncementPack'
import CorporateOfferExecutivePack from './packs/corporate/CorporateOfferExecutivePack'
import CorporateStoryPack from './packs/corporate/CorporateStoryPack'

import EventAuthorityPack from './packs/events/EventAuthorityPack'
import EventCarouselPack from './packs/events/EventCarouselPack'
import EventCommunityPack from './packs/events/EventCommunityPack'
import EventConversionOfferPack from './packs/events/EventConversionOfferPack'
import EventEducationalPack from './packs/events/EventEducationalPack'
import EventStoryPack from './packs/events/EventStoryPack'
import EventTestimonialPack from './packs/events/EventTestimonialPack'
import EventWebinarAnnouncementPack from './packs/events/EventWebinarAnnouncementPack'

import FitnessAuthorityPack from './packs/fitness/FitnessAuthorityPack'
import FitnessCarouselPack from './packs/fitness/FitnessCarouselPack'
import FitnessChallengeAnnouncementPack from './packs/fitness/FitnessChallengeAnnouncementPack'
import FitnessCommunityPack from './packs/fitness/FitnessCommunityPack'
import FitnessEducationalPack from './packs/fitness/FitnessEducationalPack'
import FitnessOfferPerformancePack from './packs/fitness/FitnessOfferPerformancePack'
import FitnessTestimonialPack from './packs/fitness/FitnessTestimonialPack'
import FitnessTransformationStoryPack from './packs/fitness/FitnessTransformationStoryPack'

interface Props {
  draft: PublicationDraft
  activeIndex?: number
  onActiveIndexChange?: (index: number) => void
  showControls?: boolean
  exportMode?: boolean
}

function normalizePackId(value: unknown) {
  return String(value || '')
    .trim()
    .replaceAll('_', '-')
}

export default function PublicationRenderer({
  draft,
  activeIndex,
  onActiveIndexChange,
  showControls = true,
  exportMode = false,
}: Props) {
  const rawPackId =
    (draft as any).variant ||
    (draft as any).themeId ||
    (draft as any).templateId ||
    ''

  const packId = normalizePackId(rawPackId)

  const props = {
    draft,
    activeIndex,
    onActiveIndexChange,
    showControls,
    exportMode
  }

  /**
   * CREATOR / PERSONAL BRAND
   */
  if (packId === 'creator-announcement' || packId === 'creator-announcement-v2') {
    return <CreatorAnnouncementPack {...props} />
  }

  if (packId === 'creator-authority' || packId === 'creator-authority-v2') {
    return <CreatorAuthorityPack {...props} />
  }

  if (packId === 'creator-carousel' || packId === 'creator-carousel-v2') {
    return <CreatorCarouselPack {...props} />
  }

  if (packId === 'creator-community' || packId === 'creator-community-v2') {
    return <CreatorCommunityPack {...props} />
  }

  if (packId === 'creator-educational' || packId === 'creator-educational-v2') {
    return <CreatorEducationalPack {...props} />
  }

  if (packId === 'creator-offer' || packId === 'creator-offer-v2') {
    return <CreatorOfferConversionPack {...props} />
  }

  if (packId === 'creator-story' || packId === 'creator-story-v2') {
    return <CreatorStoryPack {...props} />
  }

  if (packId === 'creator-testimonial' || packId === 'creator-testimonial-v2') {
    return <CreatorTestimonialPack {...props} />
  }

  /**
   * BEAUTY / SKINCARE
   */
  if (packId === 'beauty-announcement' || packId === 'beauty-announcement-v2') {
    return <BeautyAnnouncementPack {...props} />
  }

  if (packId === 'beauty-authority' || packId === 'beauty-authority-v2') {
    return <BeautyAuthorityPack {...props} />
  }

  if (packId === 'beauty-community' || packId === 'beauty-community-v2') {
    return <BeautyCommunityPack {...props} />
  }

  if (
    packId === 'beauty-educational' ||
    packId === 'beauty-educational-v2' ||
    packId === 'beauty-educational-routine'
  ) {
    return <BeautyEducationalRoutinePack {...props} />
  }

  if (
    packId === 'beauty-offer' ||
    packId === 'beauty-offer-v2' ||
    packId === 'beauty-luxury-offer'
  ) {
    return <BeautyLuxuryOfferPack {...props} />
  }

  if (packId === 'beauty-story' || packId === 'beauty-story-v2') {
    return <BeautyStoryPack {...props} />
  }

  if (packId === 'beauty-testimonial' || packId === 'beauty-testimonial-v2') {
    return <BeautyTestimonialPack {...props} />
  }

  /**
   * CORPORATE / BUSINESS
   */
  if (packId === 'corporate-authority' || packId === 'corporate-authority-v2') {
    return <CorporateAuthorityPack {...props} />
  }

  if (packId === 'corporate-carousel' || packId === 'corporate-carousel-v2') {
    return <CorporateCarouselPack {...props} />
  }

  if (
    packId === 'corporate-testimonial' ||
    packId === 'corporate-testimonial-v2' ||
    packId === 'corporate-case-study-testimonial'
  ) {
    return <CorporateCaseStudyTestimonialPack {...props} />
  }

  if (packId === 'corporate-community' || packId === 'corporate-community-v2') {
    return <CorporateCommunityPack {...props} />
  }

  if (packId === 'corporate-educational' || packId === 'corporate-educational-v2') {
    return <CorporateEducationalPack {...props} />
  }

  if (
    packId === 'corporate-announcement' ||
    packId === 'corporate-announcement-v2' ||
    packId === 'corporate-executive-announcement'
  ) {
    return <CorporateExecutiveAnnouncementPack {...props} />
  }

  if (packId === 'corporate-offer' || packId === 'corporate-offer-v2') {
    return <CorporateOfferExecutivePack {...props} />
  }

  if (packId === 'corporate-story' || packId === 'corporate-story-v2') {
    return <CorporateStoryPack {...props} />
  }

  /**
   * EVENTS / WEBINARS
   */
  if (packId === 'event-authority' || packId === 'events-authority-v2' || packId === 'event-authority-v2') {
    return <EventAuthorityPack {...props} />
  }

  if (packId === 'event-carousel' || packId === 'events-carousel-v2' || packId === 'event-carousel-v2') {
    return <EventCarouselPack {...props} />
  }

  if (packId === 'event-community' || packId === 'events-community-v2' || packId === 'event-community-v2') {
    return <EventCommunityPack {...props} />
  }

  if (
    packId === 'event-offer' ||
    packId === 'event-offer-v2' ||
    packId === 'events-offer-v2' ||
    packId === 'event-conversion-offer'
  ) {
    return <EventConversionOfferPack {...props} />
  }

  if (packId === 'event-educational' || packId === 'events-educational-v2' || packId === 'event-educational-v2') {
    return <EventEducationalPack {...props} />
  }

  if (packId === 'event-story' || packId === 'events-story-v2' || packId === 'event-story-v2') {
    return <EventStoryPack {...props} />
  }

  if (packId === 'event-testimonial' || packId === 'events-testimonial-v2' || packId === 'event-testimonial-v2') {
    return <EventTestimonialPack {...props} />
  }

  if (
    packId === 'event-announcement' ||
    packId === 'event-announcement-v2' ||
    packId === 'events-announcement-v2' ||
    packId === 'event-webinar-announcement'
  ) {
    return <EventWebinarAnnouncementPack {...props} />
  }

  /**
   * FITNESS / WELLNESS
   */
  if (packId === 'fitness-authority' || packId === 'fitness-authority-v2') {
    return <FitnessAuthorityPack {...props} />
  }

  if (packId === 'fitness-carousel' || packId === 'fitness-carousel-v2') {
    return <FitnessCarouselPack {...props} />
  }

  if (
    packId === 'fitness-announcement' ||
    packId === 'fitness-announcement-v2' ||
    packId === 'fitness-challenge-announcement'
  ) {
    return <FitnessChallengeAnnouncementPack {...props} />
  }

  if (packId === 'fitness-community' || packId === 'fitness-community-v2') {
    return <FitnessCommunityPack {...props} />
  }

  if (packId === 'fitness-educational' || packId === 'fitness-educational-v2') {
    return <FitnessEducationalPack {...props} />
  }

  if (packId === 'fitness-offer' || packId === 'fitness-offer-v2') {
    return <FitnessOfferPerformancePack {...props} />
  }

  if (packId === 'fitness-testimonial' || packId === 'fitness-testimonial-v2') {
    return <FitnessTestimonialPack {...props} />
  }

  if (
    packId === 'fitness-story' ||
    packId === 'fitness-story-v2' ||
    packId === 'fitness-transformation-story'
  ) {
    return <FitnessTransformationStoryPack {...props} />
  }

  /**
   * FALLBACKS
   */
  if (draft.format === 'carousel') {
    return <TemplateCarouselPremium draft={draft} />
  }

  return <TemplateSingleHero draft={draft} />
}